/**
 * Offline Queue Service
 * Handles offline note creation with max 5 notes queue
 */

const DB_NAME = 'LifeManagerOffline'
const DB_VERSION = 4
const QUEUE_STORE = 'noteQueue'
const TAGS_STORE = 'tags'
const NOTES_STORE = 'notes'
const DELETE_QUEUE_STORE = 'deleteQueue'
const MAX_QUEUE_SIZE = 5

let db = null
let currentUserId = null

// Set current user ID for filtering
export function setCurrentUserId(userId) {
  currentUserId = userId
}

// Clear current user ID on logout
export function clearCurrentUserId() {
  currentUserId = null
}

// Clear all data for current user on logout (SECURITY)
export async function clearCurrentUserData() {
  if (!db) await initOfflineDB()
  if (!currentUserId) return
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([NOTES_STORE, TAGS_STORE, QUEUE_STORE, DELETE_QUEUE_STORE], 'readwrite')
    
    // Clear notes for current user
    const notesStore = transaction.objectStore(NOTES_STORE)
    const notesIndex = notesStore.index('userId')
    const notesRange = IDBKeyRange.only(currentUserId)
    const notesCursor = notesIndex.openCursor(notesRange)
    
    notesCursor.onsuccess = (e) => {
      const cursor = e.target.result
      if (cursor) {
        cursor.delete()
        cursor.continue()
      }
    }
    
    // Clear tags for current user
    const tagsStore = transaction.objectStore(TAGS_STORE)
    const tagsIndex = tagsStore.index('userId')
    const tagsRange = IDBKeyRange.only(currentUserId)
    const tagsCursor = tagsIndex.openCursor(tagsRange)
    
    tagsCursor.onsuccess = (e) => {
      const cursor = e.target.result
      if (cursor) {
        cursor.delete()
        cursor.continue()
      }
    }
    
    // Clear queue and delete queue (they don't have userId, clear all)
    const queueStore = transaction.objectStore(QUEUE_STORE)
    queueStore.clear()
    
    const deleteQueueStore = transaction.objectStore(DELETE_QUEUE_STORE)
    deleteQueueStore.clear()
    
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

// Initialize IndexedDB
export async function initOfflineDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    
    request.onerror = () => reject(request.error)
    request.onsuccess = () => {
      db = request.result
      resolve(db)
    }
    
    request.onupgradeneeded = (event) => {
      const database = event.target.result
      
      // Create queue store if doesn't exist
      if (!database.objectStoreNames.contains(QUEUE_STORE)) {
        const store = database.createObjectStore(QUEUE_STORE, { keyPath: 'id', autoIncrement: true })
        store.createIndex('timestamp', 'timestamp', { unique: false })
      }
      
      // Create tags store if doesn't exist
      if (!database.objectStoreNames.contains(TAGS_STORE)) {
        const tagsStore = database.createObjectStore(TAGS_STORE, { keyPath: 'id' })
        tagsStore.createIndex('userId', 'userId', { unique: false })
        tagsStore.createIndex('name', 'name', { unique: false })
        tagsStore.createIndex('usage_count', 'usage_count', { unique: false })
      } else if (event.oldVersion < 4) {
        // Migration: Add userId index to existing store
        const transaction = event.target.transaction
        const tagsStore = transaction.objectStore(TAGS_STORE)
        if (!tagsStore.indexNames.contains('userId')) {
          tagsStore.createIndex('userId', 'userId', { unique: false })
        }
      }
      
      // Create notes store if doesn't exist
      if (!database.objectStoreNames.contains(NOTES_STORE)) {
        const notesStore = database.createObjectStore(NOTES_STORE, { keyPath: 'id' })
        notesStore.createIndex('userId', 'userId', { unique: false })
        notesStore.createIndex('category', 'category', { unique: false })
        notesStore.createIndex('createdAt', 'createdAt', { unique: false })
      } else if (event.oldVersion < 4) {
        // Migration: Add userId index to existing store
        const transaction = event.target.transaction
        const notesStore = transaction.objectStore(NOTES_STORE)
        if (!notesStore.indexNames.contains('userId')) {
          notesStore.createIndex('userId', 'userId', { unique: false })
        }
      }
      
      // Create delete queue store if doesn't exist
      if (!database.objectStoreNames.contains(DELETE_QUEUE_STORE)) {
        const deleteStore = database.createObjectStore(DELETE_QUEUE_STORE, { keyPath: 'noteId' })
        deleteStore.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }
  })
}

// Get queue count
export async function getQueueCount() {
  if (!db) await initOfflineDB()
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], 'readonly')
    const store = transaction.objectStore(QUEUE_STORE)
    const request = store.count()
    
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// Add note to offline queue
export async function addToQueue(noteData) {
  if (!db) await initOfflineDB()
  
  const count = await getQueueCount()
  if (count >= MAX_QUEUE_SIZE) {
    throw new Error(`Cannot save more than ${MAX_QUEUE_SIZE} notes offline`)
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], 'readwrite')
    const store = transaction.objectStore(QUEUE_STORE)
    
    const queueItem = {
      ...noteData,
      timestamp: Date.now(),
      synced: false
    }
    
    const request = store.add(queueItem)
    
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// Get all queued notes
export async function getQueuedNotes() {
  if (!db) await initOfflineDB()
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], 'readonly')
    const store = transaction.objectStore(QUEUE_STORE)
    const request = store.getAll()
    
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// Remove note from queue
export async function removeFromQueue(id) {
  if (!db) await initOfflineDB()
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], 'readwrite')
    const store = transaction.objectStore(QUEUE_STORE)
    const request = store.delete(id)
    
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// Clear all synced notes from queue
export async function clearSyncedNotes() {
  if (!db) await initOfflineDB()
  
  const notes = await getQueuedNotes()
  const syncedIds = notes.filter(n => n.synced).map(n => n.id)
  
  for (const id of syncedIds) {
    await removeFromQueue(id)
  }
}

// Check if online
export function isOnline() {
  return navigator.onLine
}

// =============== TAGS CACHE ===============

// Save all tags to cache for current user
export async function cacheTags(tags) {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for caching tags')
    return
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TAGS_STORE], 'readwrite')
    const store = transaction.objectStore(TAGS_STORE)
    const index = store.index('userId')
    
    // Delete only current user's tags
    const range = IDBKeyRange.only(currentUserId)
    const cursorRequest = index.openCursor(range)
    const keysToDelete = []
    
    cursorRequest.onsuccess = (e) => {
      const cursor = e.target.result
      if (cursor) {
        keysToDelete.push(cursor.primaryKey)
        cursor.continue()
      } else {
        // Delete collected keys
        keysToDelete.forEach(key => store.delete(key))
        
        // Add all tags with userId - convert to plain objects (deep clone)
        const plainTags = tags.map(tag => {
          const plain = JSON.parse(JSON.stringify(tag))
          return {
            id: `${currentUserId}_${plain.id}`,
            userId: currentUserId,
            tagId: plain.id,
            name: plain.name,
            usage_count: plain.usage_count || plain.count || 0,
            timestamp: Date.now()
          }
        })
        
        plainTags.forEach(tagData => store.add(tagData))
        
        transaction.oncomplete = () => resolve()
      }
    }
    
    cursorRequest.onerror = () => reject(cursorRequest.error)
    transaction.onerror = () => reject(transaction.error)
  })
}

// Get all cached tags for current user
export async function getCachedTags() {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for fetching cached tags')
    return []
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TAGS_STORE], 'readonly')
    const store = transaction.objectStore(TAGS_STORE)
    const index = store.index('userId')
    const range = IDBKeyRange.only(currentUserId)
    const request = index.getAll(range)
    
    request.onsuccess = () => {
      // Return with original structure
      const tags = request.result.map(tag => ({
        id: tag.tagId,
        name: tag.name,
        usage_count: tag.usage_count
      }))
      resolve(tags)
    }
    request.onerror = () => reject(request.error)
  })
}

// Check if tags cache exists for current user
export async function hasTagsCache() {
  if (!db) await initOfflineDB()
  if (!currentUserId) return false
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TAGS_STORE], 'readonly')
    const store = transaction.objectStore(TAGS_STORE)
    const index = store.index('userId')
    const range = IDBKeyRange.only(currentUserId)
    const request = index.count(range)
    
    request.onsuccess = () => resolve(request.result > 0)
    request.onerror = () => reject(request.error)
  })
}

// Update single tag in cache
export async function updateCachedTag(tagId, updates) {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for updating cached tag')
    return null
  }
  
  return new Promise(async (resolve, reject) => {
    const transaction = db.transaction([TAGS_STORE], 'readwrite')
    const store = transaction.objectStore(TAGS_STORE)
    
    const compositeKey = `${currentUserId}_${tagId}`
    const getRequest = store.get(compositeKey)
    
    getRequest.onsuccess = () => {
      const tag = getRequest.result
      if (tag) {
        const updated = { ...tag, ...updates, timestamp: Date.now() }
        const putRequest = store.put(updated)
        putRequest.onsuccess = () => resolve(updated)
        putRequest.onerror = () => reject(putRequest.error)
      } else {
        resolve(null)
      }
    }
    
    getRequest.onerror = () => reject(getRequest.error)
  })
}

// Delete tag from cache
export async function deleteCachedTag(tagId) {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for deleting cached tag')
    return
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TAGS_STORE], 'readwrite')
    const store = transaction.objectStore(TAGS_STORE)
    const compositeKey = `${currentUserId}_${tagId}`
    const request = store.delete(compositeKey)
    
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// Add new tag to cache
export async function addCachedTag(tag) {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for adding cached tag')
    return null
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TAGS_STORE], 'readwrite')
    const store = transaction.objectStore(TAGS_STORE)
    
    const tagData = {
      id: `${currentUserId}_${tag.id}`,
      userId: currentUserId,
      tagId: tag.id,
      name: tag.name,
      usage_count: tag.usage_count || 1,
      timestamp: Date.now()
    }
    
    const request = store.add(tagData)
    
    request.onsuccess = () => resolve(tagData)
    request.onerror = () => reject(request.error)
  })
}

// =============== NOTES CACHE ===============

// Save all notes to IndexedDB for current user
export async function cacheNotes(notes) {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for caching notes')
    return
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([NOTES_STORE], 'readwrite')
    const store = transaction.objectStore(NOTES_STORE)
    const index = store.index('userId')
    
    // Delete only current user's notes
    const range = IDBKeyRange.only(currentUserId)
    const cursorRequest = index.openCursor(range)
    const keysToDelete = []
    
    cursorRequest.onsuccess = (e) => {
      const cursor = e.target.result
      if (cursor) {
        keysToDelete.push(cursor.primaryKey)
        cursor.continue()
      } else {
        // Delete collected keys
        keysToDelete.forEach(key => store.delete(key))
        
        // Add all notes with userId - convert to plain objects (deep clone)
        const plainNotes = notes.map(note => {
          const plain = JSON.parse(JSON.stringify(note))
          return {
            ...plain,
            userId: currentUserId,
            tags: Array.isArray(plain.tags) ? plain.tags.join(',') : (plain.tags || '')
          }
        })
        
        plainNotes.forEach(noteData => store.add(noteData))
        
        transaction.oncomplete = () => resolve()
      }
    }
    
    cursorRequest.onerror = () => reject(cursorRequest.error)
    transaction.onerror = () => reject(transaction.error)
  })
}

// Get all cached notes for current user
export async function getCachedNotes() {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for fetching cached notes')
    return []
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([NOTES_STORE], 'readonly')
    const store = transaction.objectStore(NOTES_STORE)
    const index = store.index('userId')
    const range = IDBKeyRange.only(currentUserId)
    const request = index.getAll(range)
    
    request.onsuccess = () => {
      const notes = request.result.map(note => ({
        ...note,
        tags: typeof note.tags === 'string' ? note.tags.split(',').map(t => t.trim()).filter(t => t) : (Array.isArray(note.tags) ? note.tags : [])
      }))
      resolve(notes)
    }
    request.onerror = () => reject(request.error)
  })
}

// Add single note to cache
export async function addCachedNote(note) {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for adding cached note')
    return
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([NOTES_STORE], 'readwrite')
    const store = transaction.objectStore(NOTES_STORE)
    const plain = JSON.parse(JSON.stringify(note))
    const noteToCache = {
      ...plain,
      userId: currentUserId,
      tags: Array.isArray(plain.tags) ? plain.tags.join(',') : (plain.tags || '')
    }
    const request = store.add(noteToCache)
    
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// Update single note in cache
export async function updateCachedNote(noteId, note) {
  if (!db) await initOfflineDB()
  if (!currentUserId) {
    console.warn('No user ID set for updating cached note')
    return
  }
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([NOTES_STORE], 'readwrite')
    const store = transaction.objectStore(NOTES_STORE)
    const plain = JSON.parse(JSON.stringify(note))
    const noteToCache = {
      ...plain,
      userId: currentUserId,
      tags: Array.isArray(plain.tags) ? plain.tags.join(',') : (plain.tags || '')
    }
    const request = store.put(noteToCache)
    
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// Delete note from cache
export async function deleteCachedNote(noteId) {
  if (!db) await initOfflineDB()
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([NOTES_STORE], 'readwrite')
    const store = transaction.objectStore(NOTES_STORE)
    const request = store.delete(noteId)
    
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// =============== DELETE QUEUE ===============

// Add note ID to delete queue (for offline deletes)
export async function addToDeleteQueue(noteId) {
  if (!db) await initOfflineDB()
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([DELETE_QUEUE_STORE], 'readwrite')
    const store = transaction.objectStore(DELETE_QUEUE_STORE)
    
    const queueItem = {
      noteId,
      timestamp: Date.now()
    }
    
    const request = store.put(queueItem)
    
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// Get all queued deletes
export async function getQueuedDeletes() {
  if (!db) await initOfflineDB()
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([DELETE_QUEUE_STORE], 'readonly')
    const store = transaction.objectStore(DELETE_QUEUE_STORE)
    const request = store.getAll()
    
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// Remove from delete queue
export async function removeFromDeleteQueue(noteId) {
  if (!db) await initOfflineDB()
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([DELETE_QUEUE_STORE], 'readwrite')
    const store = transaction.objectStore(DELETE_QUEUE_STORE)
    const request = store.delete(noteId)
    
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}
